import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Plus, Loader2, Trash2, ChevronLeft, ChevronRight, CalendarDays, Bell, Copy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { DepotPicker, DepotTag } from "@/components/DepotPicker";
import { useDepot } from "@/context/DepotContext";
import { usePay } from "@/context/CompanyContext";
import { weekStartDate, weekDayLabels } from "@/lib/payPeriod";
import { todayLocal } from "@/lib/format";

const iso = (d) => { const x = new Date(d); return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}-${String(x.getDate()).padStart(2, "0")}`; };

export const RosterTab = () => {
  const pay = usePay();
  const DAYS = weekDayLabels(pay);
  const [weekStart, setWeekStart] = useState(() => weekStartDate(new Date(), pay));
  useEffect(() => { setWeekStart(weekStartDate(new Date(), pay)); }, [pay.start_day, pay.timezone]); // eslint-disable-line react-hooks/exhaustive-deps
  const [items, setItems] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [notifying, setNotifying] = useState(false);
  const [copying, setCopying] = useState(false);
  const [form, setForm] = useState({ worker_email: "", date: todayLocal(), start_time: "07:00", end_time: "15:30", note: "", depot_id: "" });
  const { needsPick } = useDepot();

  const load = async () => {
    setLoading(true);
    try {
      const [s, w] = await Promise.all([api.schedule(iso(weekStart)), api.workers()]);
      setItems(s); setWorkers(w);
    } catch { toast.error("Failed to load roster"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [weekStart]);

  const create = async () => {
    if (!form.worker_email || !form.date) { toast.error("Worker and date are required"); return; }
    if (needsPick && !form.depot_id) { toast.error("Choose a depot for this shift"); return; }
    const w = workers.find((x) => x.email === form.worker_email);
    try {
      await api.createSchedule({ ...form, depot_id: form.depot_id || undefined, worker_name: w?.name || "" });
      toast.success("Shift added"); setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add shift"); }
  };

  const remove = async (id) => {
    try { await api.deleteSchedule(id); toast.success("Shift removed"); load(); }
    catch { toast.error("Could not remove shift"); }
  };

  const notifyCrew = async () => {
    setNotifying(true);
    try {
      const r = await api.notifyRoster(iso(weekStart));
      if (!r.workers) toast.info("No shifts rostered this week yet");
      else if (!r.notified) toast.info(`${r.workers} worker${r.workers !== 1 ? "s" : ""} rostered, but none have notifications turned on yet`);
      else toast.success(`Notified ${r.notified} of ${r.workers} rostered worker${r.workers !== 1 ? "s" : ""}`);
    } catch { toast.error("Could not notify crew"); }
    finally { setNotifying(false); }
  };

  const copyLastWeek = async () => {
    setCopying(true);
    try {
      const from = new Date(weekStart); from.setDate(from.getDate() - 7);
      const r = await api.copyWeek(iso(from), iso(weekStart));
      if (!r.source) toast.info("Last week had no shifts to copy");
      else if (!r.copied) toast.info("This week already matches last week");
      else { toast.success(`Copied ${r.copied} shift${r.copied !== 1 ? "s" : ""} from last week`); load(); }
    } catch { toast.error("Could not copy last week"); }
    finally { setCopying(false); }
  };

  const days = Array.from({ length: 7 }, (_, i) => { const d = new Date(weekStart); d.setDate(d.getDate() + i); return d; });
  const byDate = (d) => items.filter((x) => x.date === iso(d));
  const label = `${weekStart.toLocaleDateString(undefined, { day: "2-digit", month: "short" })} – ${days[6].toLocaleDateString(undefined, { day: "2-digit", month: "short" })}`;

  return (
    <div className="fade-up">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h2 className="font-display text-2xl font-bold">Roster</h2>
          <p className="text-sm text-muted-foreground">Weekly shifts synced to workers' apps</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" data-testid="roster-prev" className="rounded-full" onClick={() => { const d = new Date(weekStart); d.setDate(d.getDate() - 7); setWeekStart(d); }}><ChevronLeft className="w-4 h-4" /></Button>
          <span className="text-sm font-medium font-mono w-40 text-center">{label}</span>
          <Button variant="outline" size="icon" data-testid="roster-next" className="rounded-full" onClick={() => { const d = new Date(weekStart); d.setDate(d.getDate() + 7); setWeekStart(d); }}><ChevronRight className="w-4 h-4" /></Button>
          <Button variant="outline" data-testid="copy-week-btn" onClick={copyLastWeek} disabled={copying} className="rounded-full" title="Copy last week's shifts into this week">
            {copying ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Copy className="w-4 h-4 mr-2" />} Copy last week
          </Button>
          <Button variant="outline" data-testid="notify-crew-btn" onClick={notifyCrew} disabled={notifying || items.length === 0} className="rounded-full" title="Send a push alert to everyone rostered this week">
            {notifying ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Bell className="w-4 h-4 mr-2" />} Notify crew
          </Button>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button data-testid="add-shift-btn" className="rounded-full bg-primary hover:bg-primary/90 text-white"><Plus className="w-4 h-4 mr-2" /> Add shift</Button></DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader><DialogTitle className="font-display text-xl">Add shift</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <DepotPicker value={form.depot_id} onChange={(v) => setForm({ ...form, depot_id: v })} testId="shift-depot-select" />
                <div className="space-y-2"><Label>Worker *</Label>
                  <Select value={form.worker_email} onValueChange={(v) => setForm({ ...form, worker_email: v })}>
                    <SelectTrigger data-testid="shift-worker-select"><SelectValue placeholder="Select worker" /></SelectTrigger>
                    <SelectContent>{workers.map((w) => <SelectItem key={w.email} value={w.email}>{w.name || w.email}</SelectItem>)}</SelectContent>
                  </Select></div>
                <div className="space-y-2"><Label>Date *</Label><Input data-testid="shift-date-input" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2"><Label>Start</Label><Input data-testid="shift-start-input" type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} /></div>
                  <div className="space-y-2"><Label>End</Label><Input data-testid="shift-end-input" type="time" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} /></div>
                </div>
                <div className="space-y-2"><Label>Note</Label><Input data-testid="shift-note-input" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="Site / task" /></div>
              </div>
              <DialogFooter><Button data-testid="shift-create-submit" onClick={create} className="rounded-full bg-primary hover:bg-primary/90 text-white">Add shift</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {loading ? <div className="flex justify-center py-20"><Loader2 className="w-7 h-7 animate-spin text-primary" /></div> : (
        <div className="grid grid-cols-1 md:grid-cols-7 gap-3">
          {days.map((d, i) => (
            <div key={i} className="bg-card border border-border rounded-xl p-3 min-h-[140px]">
              <p className="text-xs font-semibold text-muted-foreground mb-2">{DAYS[i]} <span className="font-mono">{d.getDate()}</span></p>
              <div className="space-y-2">
                {byDate(d).length === 0 && <p className="text-[11px] text-muted-foreground/60 flex items-center gap-1"><CalendarDays className="w-3 h-3" /> —</p>}
                {byDate(d).map((s) => (
                  <div key={s.id} data-testid={`shift-${s.id}`} className={`group rounded-lg border px-2.5 py-2 ${s.status === "declined" ? "bg-secondary/40 border-border opacity-60" : s.status === "accepted" ? "bg-emerald-500/10 border-emerald-500/25" : s.status === "pending" ? "bg-amber-500/10 border-amber-500/30" : "bg-primary/10 border-primary/20"}`}>
                    <div className="flex items-start justify-between gap-1">
                      <p className={`text-xs font-semibold truncate ${s.status === "declined" ? "line-through" : ""}`}>{s.worker_name || s.worker_email?.split("@")[0]}</p>
                      <button data-testid={`remove-shift-${s.id}`} onClick={() => remove(s.id)} className="opacity-0 group-hover:opacity-100 text-destructive"><Trash2 className="w-3 h-3" /></button>
                    </div>
                    {(s.start_time || s.end_time) && <p className="text-[11px] font-mono text-muted-foreground">{s.start_time}–{s.end_time}</p>}
                    {s.note && <p className="text-[11px] text-muted-foreground truncate">{s.note}</p>}
                    <DepotTag id={s.depot_id} testId={`shift-depot-${s.id}`} />
                    {s.employment_type === "casual" && s.status && s.status !== "confirmed" && (
                      <span data-testid={`shift-status-${s.id}`} className={`mt-1 inline-block text-[10px] font-semibold rounded-full px-1.5 py-0.5 ${s.status === "accepted" ? "bg-emerald-500/20 text-emerald-700" : s.status === "declined" ? "bg-red-500/15 text-red-600" : "bg-amber-500/20 text-amber-700"}`}>
                        {s.status === "accepted" ? "Accepted" : s.status === "declined" ? "Declined" : "Pending"}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
